import { Flame, Snowflake, RefreshCw, Package, MoreHorizontal, Settings, User, LogOut, Mail } from "lucide-react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { useUserRole } from "@/hooks/useUserRole";
import { useRefresherCount } from "@/hooks/useRefresherCount";
import { ThemeToggle } from "@/components/ThemeToggle";
import { GroupsList } from "@/components/GroupsList";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";

const items = [
  { title: "Hot", path: "hot", icon: Flame },
  { title: "Cold", path: "cold", icon: Snowflake },
  { title: "Refresher", path: "refresher", icon: RefreshCw },
  { title: "Products", path: "products", icon: Package },
];

export function MobileTabBar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { workspace, selectedGroupId } = useWorkspace();
  const { canAccessGroups } = useUserRole();
  const refresherCount = useRefresherCount();
  const [moreOpen, setMoreOpen] = useState(false);

  const pathPrefix = "/dashboard";
  const buildUrl = (path: string) =>
    workspace === "groups" && selectedGroupId
      ? `${pathPrefix}/groups/${selectedGroupId}/${path}`
      : workspace === "groups"
        ? `${pathPrefix}/groups`
        : `${pathPrefix}/personal/${path}`;

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/");
  };

  const goAndClose = (path: string) => {
    setMoreOpen(false);
    navigate(buildUrl(path));
  };

  return (
    <nav
      className="md:hidden fixed bottom-0 inset-x-0 z-40 border-t border-border bg-background/95 backdrop-blur-md pb-safe"
      aria-label="Primary"
    >
      <ul className="grid grid-cols-5 h-16">
        {items.map((item) => {
          const url = buildUrl(item.path);
          const active = location.pathname.endsWith(`/${item.path}`);
          return (
            <li key={item.path}>
              <NavLink
                to={url}
                className={cn(
                  "relative flex h-full flex-col items-center justify-center gap-1 text-[10px] font-mono uppercase tracking-[0.12em] transition-colors",
                  active ? "text-primary" : "text-muted-foreground hover:text-foreground"
                )}
              >
                <item.icon className="h-5 w-5" strokeWidth={active ? 2.25 : 1.75} />
                <span>{item.title}</span>
                {item.path === "refresher" && refresherCount > 0 && (
                  <Badge variant="destructive" className="absolute top-1 right-[22%] h-4 min-w-4 px-1 text-[9px]">
                    {refresherCount}
                  </Badge>
                )}
                {active && <span className="absolute top-0 h-[2px] w-8 rounded-full bg-primary" />}
              </NavLink>
            </li>
          );
        })}
        <li>
          <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
            <SheetTrigger className="relative flex h-full w-full flex-col items-center justify-center gap-1 text-[10px] font-mono uppercase tracking-[0.12em] text-muted-foreground hover:text-foreground transition-colors">
              <MoreHorizontal className="h-5 w-5" strokeWidth={1.75} />
              <span>More</span>
            </SheetTrigger>
            <SheetContent side="bottom" className="rounded-t-2xl pt-8 pb-[calc(env(safe-area-inset-bottom)+1.5rem)]">
              <SheetHeader className="text-left mb-4">
                <SheetTitle className="font-display text-lg">Menu</SheetTitle>
              </SheetHeader>
              <div className="space-y-1">
                <button
                  onClick={() => goAndClose("setup")}
                  className="w-full flex items-center gap-3 rounded-lg px-3 py-3 text-sm hover:bg-accent active:bg-accent transition-colors"
                >
                  <Settings className="h-4 w-4 text-muted-foreground" />
                  <span>Sheet Setup</span>
                </button>
                <button
                  onClick={() => { setMoreOpen(false); navigate(`${pathPrefix}/profile`); }}
                  className="w-full flex items-center gap-3 rounded-lg px-3 py-3 text-sm hover:bg-accent active:bg-accent transition-colors"
                >
                  <User className="h-4 w-4 text-muted-foreground" />
                  <span>Profile</span>
                </button>
                <a
                  href="mailto:support@echocrm.app"
                  className="w-full flex items-center gap-3 rounded-lg px-3 py-3 text-sm hover:bg-accent transition-colors"
                >
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  <span>Support</span>
                </a>
              </div>

              {workspace === "groups" && canAccessGroups && (
                <div className="mt-4 max-h-[40vh] overflow-y-auto rounded-lg border border-border p-2">
                  <GroupsList />
                </div>
              )}

              <div className="my-4 h-px bg-border" />

              <div className="flex items-center justify-between gap-3">
                <span className="text-xs font-mono uppercase tracking-[0.12em] text-muted-foreground">Theme</span>
                <ThemeToggle />
              </div>

              <button
                onClick={handleLogout}
                className="mt-6 w-full flex items-center justify-center gap-2 rounded-lg border border-border px-3 py-3 text-sm hover:bg-accent transition-colors"
              >
                <LogOut className="h-4 w-4" />
                <span>Log out</span>
              </button>
            </SheetContent>
          </Sheet>
        </li>
      </ul>
    </nav>
  );
}